import axios from 'axios';
import { toast } from 'sonner';
import { clearAuth } from './auth';

// REACT_APP_BACKEND_URL is baked in at build time. If it's missing,
// every API call would silently end up at `undefined/api/...` –
// fails as a CORS preflight or relative-URL navigation, with cryptic
// error messages. Loud-fail on app boot instead so the misconfigured
// build is obvious before the user even hits a real endpoint.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  // eslint-disable-next-line no-console
  console.error(
    'REACT_APP_BACKEND_URL is not set. API calls will fail. ' +
    'Set it in frontend/.env (or your deployment env) and rebuild.'
  );
  // Surface to the user too. A blank screen with a console error is
  // worse than a visible toast on first page load.
  if (typeof window !== 'undefined') {
    setTimeout(() => {
      try {
        // sonner toast may not be mounted yet on cold boot
        toast.error('App ist falsch konfiguriert: Backend-URL fehlt.');
      } catch (_) {
        /* no-op */
      }
    }, 1500);
  }
}
const API_BASE = `${BACKEND_URL || ''}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Add auth token and gateway key to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const gatewayKey = process.env.REACT_APP_GATEWAY_KEY;
  if (gatewayKey) {
    config.headers['X-Gateway-Key'] = gatewayKey;
  }
  return config;
});

// Handle 401 responses globally. Redirect to login on expired/invalid tokens.
// Handle 403 responses with structured anti-abuse gate detail. Show a
// friendly toast pointing the user at the verify/add-card flow instead
// of letting downstream handlers print "[object Object]" from the
// HTTPException(detail={...}) shape.
let isRedirecting = false;
let lastGateToastKey = '';
let lastGateToastAt = 0;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if this is a login/register request (those have their own error handling)
      const isAuthRequest = error.config?.url?.includes('/auth/');
      if (!isAuthRequest && !isRedirecting) {
        isRedirecting = true;
        clearAuth();
        window.location.href = '/auth';
        // Return a never-resolving promise to prevent downstream error handlers
        // from showing confusing toasts while we redirect
        return new Promise(() => {});
      }
    }

    // Anti-abuse 403 gates (`email_not_verified`, `card_on_file_required`,
    // `pro_required`). These come back as
    // `{detail: {error: '<key>', message: '<de text>'}}`. Surface the
    // message in a toast, expose the error code as a top-level
    // `error_code` so component-level catch blocks can branch on it
    // (e.g. route a `pro_required` user to /pricing instead of just
    // toast-and-give-up), and collapse `detail` to a plain string so
    // downstream callers don't render the dict.
    // Dedupe within 3s so multiple parallel requests don't stack toasts.
    if (error.response?.status === 403) {
      const detail = error.response.data?.detail;
      if (detail && typeof detail === 'object' && detail.error) {
        const now = Date.now();
        if (lastGateToastKey !== detail.error || now - lastGateToastAt > 3000) {
          lastGateToastKey = detail.error;
          lastGateToastAt = now;
          toast.error(detail.message || detail.error);
        }
        // Preserve the gate's machine-readable code on the error so
        // component handlers can branch (e.g. `pro_required` → /pricing).
        error.response.data.error_code = detail.error;
        // Replace detail so component-level catch blocks see a plain string.
        error.response.data.detail = detail.message || detail.error;
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  // DSGVO Art. 17. Soft-delete with 7-day grace. Returns the
  // hard-delete timestamp so the UI can show a confirmation message.
  deleteMyAccount: () => api.delete('/auth/me'),
  // DSGVO Art. 20. Full data export as a ZIP. We need responseType:
  // 'arraybuffer' so axios doesn't try to JSON-parse the binary body.
  exportMyData: () => api.get('/auth/me/export', { responseType: 'arraybuffer' }),
  // Email verification flow.
  resendVerification: () => api.post('/auth/send-verification-email'),
  // No auth header. Token in URL is the auth.
  verifyEmail: (token) => api.post(`/auth/verify-email/${token}`),
  // Password reset flow. Both endpoints are unauthenticated. The
  // forgot-password endpoint always returns 200 with a generic
  // message regardless of whether the email exists (anti-enumeration);
  // reset-password is gated by the one-shot SHA-256-hashed token from
  // the email link.
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Team API (v1.5. Modular per-seat subscription).
//
// Owner-only ops are gated server-side by `team_role === 'owner'`.
// `getMyTeam` returns `{ team: null }` for solo/free users so the
// frontend can branch without 404s. After accept/leave/remove, the
// caller should refetch /api/auth/me to pick up the new
// `subscription_tier` and `team_id` on the local user state.
export const teamAPI = {
  // Owner kicks off Stripe Checkout for the Team plan. `seats` ≥ 2.
  // origin_url MUST be the frontend origin so Stripe's success_url
  // points back at the React app, not the backend (which would 403
  // through the gateway middleware). window.location.origin is the
  // single right answer regardless of dev/staging/prod.
  startCheckout: ({ name, seats } = {}) =>
    api.post('/team/checkout', {
      name,
      seats,
      origin_url: typeof window !== 'undefined' ? window.location.origin : '',
    }),
  getMyTeam: () => api.get('/team'),
  invite: (email) => api.post('/team/invite', { email }),
  cancelInvite: (invitationId) =>
    api.delete(`/team/invite/${invitationId}`),
  acceptInvite: (token) => api.post('/team/invite/accept', { token }),
  removeMember: (memberUserId) =>
    api.delete(`/team/member/${memberUserId}`),
  leave: () => api.post('/team/leave'),
};

// AI helper endpoints. Rate-limited server-side (10/hour per IP).
// Keep the client-side call tight too; double-clicks shouldn't fire
// multiple completions.
export const aiAPI = {
  draftDescription: (payload) => api.post('/ai/draft-description', payload),
};

// Properties API
export const propertiesAPI = {
  create: (data) => api.post('/properties', data),
  // `config` is forwarded to axios so callers can pass `{ signal }`
  // for AbortController integration (used by OnboardingWizard's
  // unmount-cancel path).
  getAll: (config) => api.get('/properties', config),
  getOne: (id) => api.get(`/properties/${id}`),
  // Partial update. Only sends the fields the user changed. Backend
  // accepts any subset of {title, description, price, location,
  // highlights, images}. A real price change auto-stamps previous_price.
  update: (id, patch) => api.put(`/properties/${id}`, patch),
  delete: (id) => api.delete(`/properties/${id}`),
  // v1.5. First-render onboarding. Idempotent on the backend: re-calls
  // for a user who already has the demo return the existing row, so
  // mounting FirstRenderPage twice doesn't duplicate.
  ensureDemo: () => api.post('/properties/demo'),
  // v1.6. Willhaben listing import (FirstRenderPage's third mode).
  // Two-step flow: preview returns scraped data without writing to the
  // DB so the user can review on the confirmation screen, then import
  // re-scrapes and persists with the user's image selection.
  // `agreement: true` is mandatory on import. Server rejects 400
  // without it. `selected_images` is an array of integer indices into
  // the scraped images[] array; omit/empty to import all.
  willhabenPreview: (url) => api.post('/properties/willhaben-preview', { url }),
  willhabenImport: ({ url, agreement, selectedImages }) =>
    api.post('/properties/import-from-willhaben', {
      url,
      agreement: !!agreement,
      selected_images: selectedImages || null,
    }),
};

// Willhaben broker (Anbieter) bulk-import API. Three-stage flow:
//   1. brokerPreview. Paste URL, get broker info + all listings (no agent
//      grouping yet, fast).
//   2. brokerAgents. Lazy enrichment when user clicks "Group by agent",
//      returns {listing_id: agent_name} so the UI can section-header.
//   3. brokerImport. Start a background job. Returns {job_id}; UI then
//      polls brokerImportStatus(job_id) every 2-3s for progress updates.
//
// Plus subscription endpoints (paid-tier) for the auto-update flow:
//   subscriptionPreview → fetches broker + agents so user can pick theirs
//   subscriptionSave    → persists {org_id, agent_filter} on auto-post config
//   subscriptionDelete  → removes the link
//   rescan              → manual trigger for the same scan the cron runs
export const willhabenBrokerAPI = {
  preview: (url) => api.post('/willhaben-broker/preview', { url }),
  agents: (listings) => api.post('/willhaben-broker/agents', { listings }),
  import: (listings) => api.post('/willhaben-broker/import', { listings }),
  importStatus: (jobId) => api.get(`/willhaben-broker/import/${jobId}`),
  importCancel: (jobId) => api.patch(`/willhaben-broker/import/${jobId}`, { status: 'cancelled' }),
  subscriptionPreview: (url) => api.post('/willhaben-broker/subscription-preview', { url }),
  subscriptionSave: ({ orgId, agentFilter }) =>
    api.put('/willhaben-broker/subscription', { org_id: orgId, agent_filter: agentFilter }),
  subscriptionDelete: () => api.delete('/willhaben-broker/subscription'),
  rescan: () => api.post('/willhaben-broker/rescan', {}),
};

// Content Generation API
export const contentAPI = {
  // axios `params` so the property_id is properly URL-encoded. Was
  // an inline template literal before, which broke if the id contained
  // any URL-special chars (it shouldn't, but the property_id is also
  // sometimes a Justimmo string id, which has been seen with hyphens
  // and rarely Unicode).
  generateContent: (propertyId) =>
    api.post('/generate/content', null, { params: { property_id: propertyId } }),
  getContent: (propertyId) => api.get(`/generate/content/${propertyId}`),
};

// Public viral-demo API ("paste a Willhaben URL, see a reel").
// All endpoints work without auth. Except `claim`, which needs the
// signup-fresh JWT to attach the render to the new user account.
export const demoAPI = {
  startRender: (url) => api.post('/public/demo/render', { url }),
  getStatus: (token) => api.get(`/public/demo/${token}`),
  claim: (token) => api.post(`/demo/${token}/claim`),
};

// Video API
export const videoAPI = {
  getTemplates: () => api.get('/templates'),
  validateMedia: (templateId, mediaCount) => api.post('/templates/validate-media', { template_id: templateId, media_count: mediaCount }),
  generateVideo: (data) => api.post(`/generate/video`, data),
  getVideos: (propertyId) => api.get(`/generate/video/${propertyId}`),
  getVideoStatus: (renderId) => api.get(`/video/status/${renderId}`),
  // Curated voice catalog with playable demo URLs. Open endpoint –
  // surfaces on the public onboarding flow.
  getVoicePresets: () => api.get('/voice-presets'),
};

// Music Library API
export const musicAPI = {
  upload: (formData) => api.post('/music/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getLibrary: () => api.get('/music/library'),
  getPresets: () => api.get('/music/presets'),
  delete: (id) => api.delete(`/music/${id}`),
};

// Posts API
export const postsAPI = {
  create: (data) => api.post('/posts', data),
  getAll: (status) => api.get('/posts', { params: { status } }),
  publish: (id) => api.patch(`/posts/${id}/publish`),
};

// Social API (Upload Post integration)
export const socialAPI = {
  connect: () => api.post('/social/connect'),
  getAccounts: () => api.get('/social/accounts'),
  getFacebookPages: () => api.get('/social/facebook-pages'),
  getLinkedInPages: () => api.get('/social/linkedin-pages'),
  generateCaption: (propertyId) => api.post('/social/generate-caption', { property_id: propertyId }),
  publish: (data) => api.post('/social/publish', data),
  getHistory: () => api.get('/social/publish-history'),
  linkProfile: (uploadPostUsername) => api.post('/social/link-profile', { upload_post_username: uploadPostUsername }),
  debug: () => api.get('/social/debug'),
};

// Justimmo Export API
export const justimmoExportAPI = {
  connect: (data) => api.post('/justimmo-export/connect', data),
  getStatus: () => api.get('/justimmo-export/status'),
  disconnect: () => api.delete('/justimmo-export/disconnect'),
  listProperties: (params) => api.get('/justimmo-export/properties', { params }),
  getPropertyDetail: (id) => api.get(`/justimmo-export/properties/${id}`),
  getTeam: () => api.get('/justimmo-export/team'),
  importProperty: (id) => api.post(`/justimmo-export/import/${id}`),
  // Patch the active connection. Used to set default_owner_id (which
  // agent in the agency the user is) so subsequent listings auto-scope.
  updateConnection: (data) => api.patch('/justimmo-export/connection', data),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Quickstart checklist (post-payment onboarding for paid users).
// Live-derived on the backend from existing collections, no separate
// state to maintain. Dismissal is permanent. Once the user clicks
// "Schließen" we don't surface the widget again.
export const quickstartAPI = {
  get: () => api.get('/me/quickstart'),
  dismiss: () => api.post('/me/quickstart/dismiss'),
};

// Import blocklist ("deleted, don't re-import") API. Tombstones are
// written automatically when an imported property is deleted; removing
// one re-allows the listing on the next sync / manual import.
export const importBlocklistAPI = {
  list: () => api.get('/import-blocklist'),
  unblock: (entryId) => api.delete(`/import-blocklist/${entryId}`),
};

// Profile API
export const profileAPI = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
  uploadPhoto: (formData) => api.post('/profile/upload/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadLogo: (formData) => api.post('/profile/upload/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// KI-Studio (teaser until launch: early-tester sign-up only)
export const studioAPI = {
  getInterest: () => api.get('/studio/interest'),
  registerInterest: () => api.post('/studio/interest'),
};

// Gallery API
export const galleryAPI = {
  getAllVideos: () => api.get('/gallery/videos'),
};

// Auto Post API
export const autoPostAPI = {
  getConfig: () => api.get('/auto-post/config'),
  updateConfig: (data) => api.post('/auto-post/config', data),
  getQueue: () => api.get('/auto-post/queue'),
  generatePost: (propertyId) => api.post('/auto-post/queue/generate', null, {
    params: propertyId ? { property_id: propertyId } : {}
  }),
  generateVideo: (queueId) => api.post(`/auto-post/queue/${queueId}/generate-video`),
  deleteQueueItem: (queueId) => api.delete(`/auto-post/queue/${queueId}`),
  // Edit captions on an in-flight queue item. Body is a partial: only
  // the fields you want to change. Returns the full updated row.
  patchQueueItem: (queueId, patch) =>
    api.patch(`/auto-post/queue/${queueId}`, patch),
  // Re-roll captions for an existing queue item using the user's
  // CURRENT caption_style settings. Useful when the AI's first take
  // isn't quite right and the user wants a fresh set without manual
  // edits to every platform variant.
  regenerateCaptions: (queueId) =>
    api.post(`/auto-post/queue/${queueId}/regenerate-captions`),
  // Approve/Reject a preview_pending queue item directly from the
  // dashboard (without going through the email-token preview flow).
  setQueueDecision: (queueId, decision) =>
    api.post(`/auto-post/queue/${queueId}/decision`, { decision }),
  // A5. Render-pending previews waiting on user decision. Each entry
  // includes a fresh JWT-tokenized preview_url so the banner can link
  // straight to /preview?token=… without re-minting client-side.
  getPendingPreviews: () => api.get('/auto-post/pending-previews'),
  retryQueueItem: (queueId) => api.post(`/auto-post/queue/${queueId}/retry`),
  getHistory: (limit = 50) => api.get('/auto-post/history', { params: { limit } }),
  getRecommendedTemplates: (propertyId) => api.get('/auto-post/templates/recommended', {
    params: { property_id: propertyId }
  }),
  // Phase 3. Brand voice analyzer. Pasted captions → AI-synthesized
  // voice profile. The endpoint returns the profile but does NOT save
  // it; the UI shows the summary, lets the user accept or re-roll,
  // then persists via updateConfig.
  analyzeBrandVoice: (captions, language = 'de') =>
    api.post('/auto-post/brand-voice/analyze', { captions, language }),
  // One-click auto-import from connected social profile. Pulls the
  // user's recent posts via Upload Post's /media endpoint, runs them
  // through the analyzer, returns the profile + the captions we used
  // (so the UI can show "Imported 8 posts from Instagram").
  autoImportBrandVoice: () =>
    api.post('/auto-post/brand-voice/auto-import'),
  // Phase 4. Per-property caption preview. Lets the user see what
  // the AI would produce for a given property under their current
  // (or in-progress) caption_style settings without firing a render.
  previewCaptions: (propertyId, captionStyleOverride = null) =>
    api.post('/auto-post/preview-captions', {
      property_id: propertyId || null,
      caption_style_override: captionStyleOverride,
    }),
};

// Onboarding API
export const onboardingAPI = {
  // When called without a body the endpoint just flips the onboarding flag
  // (legacy behaviour). When given the wizard's final state it also commits
  // favourite templates, music pick, and voiceover preference into the
  // user's AutoPostConfig in one round-trip.
  complete: (preferences) => api.post('/onboarding/complete', preferences || undefined),
  startSamples: (propertyId) =>
    api.post('/onboarding/samples/start', { property_id: propertyId }),
  getSamples: (batchId) => api.get(`/onboarding/samples/${batchId}`),
  voteSample: (batchId, sampleId, verdict) =>
    api.post(`/onboarding/samples/${batchId}/vote`, { sample_id: sampleId, verdict }),
  abandonSamples: (batchId) => api.post(`/onboarding/samples/${batchId}/abandon`),
};

// Subscription/Payment API
export const subscriptionAPI = {
  getPlans: () => api.get('/plans'),
  getStatus: () => api.get('/subscription/status'),
  createCheckout: (originUrl, plan = 'pro') => api.post('/subscription/checkout', { origin_url: originUrl, plan }),
  getCheckoutStatus: (sessionId) => api.get(`/subscription/checkout/status/${sessionId}`),
  // Stripe Customer Portal. Handles cancel, payment-method update,
  // invoice history. Frontend calls this and then `window.location =
  // res.data.url` to redirect into the portal.
  openPortal: () => api.post('/subscription/portal'),
  // In-app subscription management (2026-09): switch between the
  // single-seat plans in place, schedule the downgrade to Free, undo it.
  changePlan: (plan) => api.post('/subscription/change-plan', { plan }),
  cancel: () => api.post('/subscription/cancel'),
  resume: () => api.post('/subscription/resume'),
  // Free-tier "card on file". Stripe Checkout in setup mode (no charge).
  // Returns a hosted checkout URL the frontend redirects to.
  createSetupIntent: () => api.post('/subscription/setup-intent'),
};

// A5. Auto-post preview links sent by email. Token-gated, no auth.
//
// publishNow: skips the scheduled-time wait + posts immediately. For
// users who saw the email after the slot passed, or who just want to
// ship it now without waiting until 15:00.
export const previewAPI = {
  get: (token) => api.get(`/preview/${token}`),
  approve: (token) => api.post(`/preview/${token}/approve`),
  reject: (token) => api.post(`/preview/${token}/reject`),
  publishNow: (token) => api.post(`/preview/${token}/publish-now`),
  // Token-authed edit of the queue item's captions. Same shape as
  // the user-authed patchQueueItem. Only fields you include get
  // changed. Server-side status whitelist refuses edits on
  // already-fired posts.
  patch: (token, patch) => api.patch(`/preview/${token}`, patch),
  // Re-run the AI generator from the email-landing page using the
  // user's current caption_style. Lets the recipient get a fresh
  // take without leaving the preview page.
  regenerateCaptions: (token) => api.post(`/preview/${token}/regenerate-captions`),
  // Fast-track edit (#22) / different property (#23): fetch the templates +
  // properties the user can switch to, and trigger a fresh render on the
  // same queue item. `params` may carry {property_id, template_id} overrides
  // so the editing data (images/texts/voiceover) refreshes for that combo
  // without rendering anything yet.
  getOptions: (token, params) => api.get(`/preview/${token}/options`, { params }),
  rerender: (token, body) => api.post(`/preview/${token}/rerender`, body),
};

export default api;
